/**
 * Las notas de voz del chat interno, del lado de la pantalla (dirección,
 * 28/9/2026: «permite en el chat que puedas grabar audios. Y que luego el
 * audio tenga una opción de ver transcripción»).
 *
 * Lo que no es pantalla: grabar, la dirección del audio y pedir la
 * transcripción. Para grabar se usa lo mismo que la grabación de consulta
 * (lib/grabadoraConsulta.js): el formato de cada navegador (WebM/Opus en
 * Chrome, MP4/AAC en Safari de iPad e iPhone), si el navegador puede grabar
 * y qué decir cuando el micrófono no se deja. Una nota es corta (5 minutos
 * como mucho): se graba entera en memoria y se sube al terminar, en un solo
 * envío, con el mismo id si hay que reintentar.
 *
 * La regla la pone el servidor (routers/chat_router.py y
 * services/notas_voz_chat.py): el tipo por el contenido, los topes y quién.
 *
 * AUDIOS YA GRABADOS (dirección, 29/9/2026: «admite poder incorporar pdf y
 * audio en el chat»): un archivo de audio que se elige, se arrastra o se
 * pega (el de WhatsApp, el de la grabadora) va como una nota de voz más, con
 * `origen=archivo`. Aquí: qué parece un audio, sus topes (10 MB y 10
 * minutos, los del servidor) y cuánto dura, que lo lee el navegador.
 */
import { api } from './api';
import {
  elegirFormato, gestorGrabacion, mensajeMicrofono, sePuedeGrabar,
} from './grabadoraConsulta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Los mismos topes que el servidor (notas_voz_chat.TOPE_SEGUNDOS y
// TOPE_BYTES). Al llegar a los cinco minutos se para sola y queda lista
// para enviar.
export const TOPE_SEGUNDOS_NOTA = 5 * 60;
export const TOPE_BYTES_NOTA = 8 * 1024 * 1024;
// Menos de esto no es una nota: es un toque sin querer.
export const MINIMO_MS = 700;
// Un archivo de audio subido (29/9/2026): los mismos topes que el servidor
// (notas_voz_chat.TOPE_BYTES_ARCHIVO y TOPE_SEGUNDOS_ARCHIVO).
export const TOPE_MB_AUDIO = 10;
export const TOPE_BYTES_AUDIO = TOPE_MB_AUDIO * 1024 * 1024;
export const TOPE_SEGUNDOS_AUDIO = 10 * 60;
export const FORMATOS_AUDIO = 'M4A, MP3, WAV, OGG/OPUS, WEBM o AAC';

// Lo que ofrece el clip para los audios: tipos y extensiones, porque cada
// sistema llama distinto a lo mismo (un .m4a es «audio/x-m4a», «audio/mp4»
// o nada; un .opus de WhatsApp, a veces nada). Sin `audio/*` a secas: en el
// móvil abriría también la grabadora.
export const ACEPTA_AUDIOS = [
  'audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/x-m4a', 'audio/m4a', 'audio/aac', 'audio/x-aac',
  'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/ogg', 'audio/opus', 'audio/webm',
  '.mp3', '.m4a', '.aac', '.wav', '.ogg', '.oga', '.opus', '.weba', '.webm',
].join(',');

const EXTENSIONES_AUDIO = /\.(mp3|m4a|aac|wav|ogg|oga|opus|weba|webm)$/i;

/** Lo que se puede ajustar (y lo que los tests acortan). */
export const CONFIG_NOTA = {
  relojMs: 250,
  // Voz: 48 kbps, menos de 2 MB en cinco minutos.
  tasaBits: 48000,
  // Cada cuánto entrega el navegador lo grabado (para ir viendo el peso).
  trozoMs: 1000,
  topeSegundos: TOPE_SEGUNDOS_NOTA,
  // «Ver transcripción» mientras otra persona la está pidiendo (el servidor
  // contesta 409): se espera y se vuelve a pedir sola, hasta unos dos
  // minutos (el servidor espera al motor 100 s como mucho).
  esperaTranscripcionMs: 4000,
  intentosTranscripcion: 30,
  // Lo que se espera al navegador para saber cuánto dura un audio elegido
  // (29/9/2026). Si no lo dice en ese rato, se sube igual y manda el tamaño.
  esperaDuracionMs: 5000,
  ahora: () => Date.now(),
};

/** La dirección del audio de una nota. Con sesión (la cookie): sin ella, el
 *  servidor contesta 401. Se sirve por trozos (`Range`), como pide Safari. */
export function urlNotaDeVoz(sala, id, { descargar = false } = {}) {
  const base = `${BACKEND_URL}/api/chat/salas/${encodeURIComponent(sala)}/audios/${encodeURIComponent(id)}`;
  return descargar ? `${base}?descargar=1` : base;
}

/** «Ver transcripción»: la primera vez la hace el servidor (y la guarda en
 *  el mensaje); después devuelve la guardada. `{ transcripcion, ya_estaba }`. */
export async function transcribirNota(sala, audioId) {
  const { data } = await api.post(
    `/api/chat/salas/${encodeURIComponent(sala)}/audios/${encodeURIComponent(audioId)}/transcripcion`,
    null, { timeout: 130000 });
  return data;
}

/** 7 → «0:07»; 75 → «1:15». Como en WhatsApp. */
export function duracionCorta(segundos) {
  const s = Math.max(0, Math.round(Number(segundos) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** ¿Dice ser un audio? Por su tipo (cualquier «audio/…»: Windows llama
 *  «audio/vnd.dlna.adts» a un .aac) o, si el navegador no lo sabe (o dice
 *  «vídeo» de un .webm), por la extensión. Lo de verdad lo mira el servidor
 *  por dentro: un vídeo disfrazado no pasa allí. */
export function pareceUnAudio(archivo) {
  const tipo = String(archivo?.type || '').toLowerCase().split(';')[0].trim();
  if (tipo.startsWith('audio/')) return true;
  if (tipo.startsWith('video/') && !/\.(weba|webm|ogg|oga|opus)$/i.test(archivo?.name || '')) return false;
  return (!tipo || tipo === 'application/octet-stream' || tipo === 'video/webm' || tipo === 'video/ogg')
    && EXTENSIONES_AUDIO.test(archivo?.name || '');
}

/** Por qué no vale este audio, o null si vale (el tamaño; la duración se
 *  mira aparte, porque hay que leerla). */
export function motivoNoValidoAudio(archivo) {
  if (!archivo) return 'No se ha podido leer el archivo.';
  const nombre = archivo.name ? `«${archivo.name}»` : 'El audio';
  if (!archivo.size) return `${nombre} está vacío.`;
  if (archivo.size > TOPE_BYTES_AUDIO) {
    const mb = (archivo.size / (1024 * 1024)).toFixed(1).replace('.', ',');
    const wav = /\.wav$/i.test(archivo.name || '') || /wav/i.test(archivo.type || '');
    return `${nombre} pesa ${mb} MB y el máximo de un audio son ${TOPE_MB_AUDIO} MB.`
      + (wav ? ' Un WAV pesa mucho: pásalo a M4A o MP3 y ocupará mucho menos.' : '');
  }
  return null;
}

/** Cuánto dura un archivo de audio, en milisegundos, leyéndolo con el
 *  propio navegador. null si no lo sabe (un formato que este navegador no
 *  reproduce, o tarda demasiado): entonces manda el tamaño. */
export function medirDuracion(archivo, { esperaMs = CONFIG_NOTA.esperaDuracionMs } = {}) {
  return new Promise((listo) => {
    let url = '';
    let hecho = false;
    let audio = null;
    const acabar = (ms) => {
      if (hecho) return;
      hecho = true;
      clearTimeout(reloj);
      if (audio) {
        audio.onloadedmetadata = null;
        audio.onerror = null;
        try { audio.removeAttribute('src'); } catch (e) { /* ya suelto */ }
      }
      try { if (url) URL.revokeObjectURL(url); } catch (e) { /* ya suelta */ }
      listo(ms);
    };
    const reloj = setTimeout(() => acabar(null), esperaMs);
    try {
      url = URL.createObjectURL(archivo);
      audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => {
        const d = audio.duration;
        acabar(Number.isFinite(d) && d > 0 ? Math.round(d * 1000) : null);
      };
      audio.onerror = () => acabar(null);
      audio.src = url;
    } catch (e) {
      acabar(null);
    }
  });
}

/** La extensión del archivo que se sube (el servidor mira el contenido; el
 *  nombre es para quien lo descargue). */
export function extensionDe(mime) {
  const base = String(mime || '').split(';')[0].trim().toLowerCase();
  if (base === 'audio/mp4') return 'm4a';
  if (base === 'audio/ogg') return 'ogg';
  return 'webm';
}

function pararPistas(stream) {
  try {
    (stream?.getTracks?.() || []).forEach((p) => { try { p.stop(); } catch (e) { /* ya parada */ } });
  } catch (e) { /* nada */ }
}

/**
 * Una nota de voz, de pulsar el micrófono a tener el audio. La pantalla
 * pinta `estado` ({ fase: 'pidiendo' | 'grabando' | 'parada' | 'fallida',
 * segundos, error }) y recibe la nota en `alParar(nota)` —siempre una vez,
 * se pare como se pare: al pulsar enviar, al llegar al tope o porque el
 * navegador corte—. `nota` es { blob, mime, duracionMs, porTope, aviso } o
 * null si no se grabó nada. Descartar no llama a `alParar`.
 */
export class GrabadoraNota {
  constructor({ alCambiar = () => {}, alParar = () => {} } = {}) {
    this.alCambiar = alCambiar;
    this.alPararFn = alParar;
    this.trozos = [];
    this.bytes = 0;
    this.estado = { fase: 'parada', segundos: 0, error: '' };
  }

  cambiar(parte) {
    this.estado = { ...this.estado, ...parte };
    try { this.alCambiar(this.estado); } catch (e) { /* la pantalla ya no está */ }
  }

  tiempoMs() {
    return this.desde ? Math.max(0, CONFIG_NOTA.ahora() - this.desde) : 0;
  }

  fallar(motivo) {
    this.cambiar({ fase: 'fallida', error: motivo });
    return false;
  }

  /** Pide el micrófono y empieza. false (con `estado.error` diciendo qué
   *  hacer) si no se puede. */
  async empezar() {
    const soporte = sePuedeGrabar();
    if (!soporte.ok) return this.fallar(soporte.motivo);
    // El micrófono es el mismo: una nota grabada en mitad de una consulta
    // se llevaría la voz de la consulta al chat.
    if (gestorGrabacion.enMarcha()) {
      return this.fallar('Hay una grabación de consulta en marcha: termínala antes de grabar una nota de voz.');
    }
    const mime = elegirFormato(window.MediaRecorder);
    if (!mime) {
      return this.fallar('Este navegador no graba audio en ningún formato que se pueda enviar. Usa Chrome o Safari actualizados.');
    }
    this.cambiar({ fase: 'pidiendo', error: '' });
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (e) {
      return this.fallar(mensajeMicrofono(e, 'el micrófono del chat'));
    }
    if (this.descartada) {
      pararPistas(this.stream);
      return false;
    }
    try {
      this.recorder = new window.MediaRecorder(this.stream, { mimeType: mime, audioBitsPerSecond: CONFIG_NOTA.tasaBits });
    } catch (e) {
      try { this.recorder = new window.MediaRecorder(this.stream); } catch (e2) {
        pararPistas(this.stream);
        return this.fallar(`No se ha podido empezar a grabar: ${e2?.message || e?.message || 'error del navegador'}.`);
      }
    }
    this.mime = this.recorder.mimeType || mime;
    this.recorder.ondataavailable = (ev) => {
      if (!ev?.data || !ev.data.size || this.descartada) return;
      this.trozos.push(ev.data);
      this.bytes += ev.data.size;
      if (this.bytes >= TOPE_BYTES_NOTA * 0.95 && !this.parando) {
        this.aviso = 'La nota ha llegado a su tope de tamaño: se ha parado y está lista para enviar.';
        this.parar();
      }
    };
    this.recorder.onstop = () => { this.alPararse(); };
    this.recorder.onerror = (ev) => {
      this.aviso = `El navegador ha cortado la grabación (${ev?.error?.name || 'error'}): se queda lo grabado hasta ahí.`;
      this.parar();
    };
    this.desde = CONFIG_NOTA.ahora();
    try {
      this.recorder.start(CONFIG_NOTA.trozoMs);
    } catch (e) {
      pararPistas(this.stream);
      return this.fallar(`No se ha podido empezar a grabar: ${e?.message || 'error del navegador'}.`);
    }
    this.reloj = setInterval(() => this.tic(), CONFIG_NOTA.relojMs);
    this.cambiar({ fase: 'grabando', segundos: 0 });
    return true;
  }

  tic() {
    const ms = this.tiempoMs();
    const segundos = Math.floor(ms / 1000);
    if (segundos !== this.estado.segundos) this.cambiar({ segundos });
    if (ms >= CONFIG_NOTA.topeSegundos * 1000 && !this.parando) {
      this.porTope = true;
      this.aviso = `Se ha llegado a los ${Math.round(CONFIG_NOTA.topeSegundos / 60)} minutos, el máximo de una nota de voz: se ha parado y está lista para enviar.`;
      this.parar();
    }
  }

  /** Para el micrófono. La nota llega por `alParar` cuando el navegador
   *  entrega lo último (al parar). */
  parar() {
    if (this.parando) return;
    this.parando = true;
    this.duracionMs = Math.min(this.tiempoMs(), CONFIG_NOTA.topeSegundos * 1000);
    clearInterval(this.reloj);
    let parado = false;
    try {
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.stop();
        parado = true;
      }
    } catch (e) { /* ya estaba parado */ }
    if (!parado) this.alPararse();
  }

  alPararse() {
    if (this.terminada) return;
    this.terminada = true;
    clearInterval(this.reloj);
    pararPistas(this.stream);
    if (this.descartada) return;
    if (this.duracionMs == null) this.duracionMs = Math.min(this.tiempoMs(), CONFIG_NOTA.topeSegundos * 1000);
    const blob = this.trozos.length ? new Blob(this.trozos, { type: this.mime }) : null;
    this.trozos = [];
    const nota = blob && blob.size ? {
      blob, mime: this.mime, duracionMs: this.duracionMs, porTope: Boolean(this.porTope), aviso: this.aviso || '',
    } : null;
    this.cambiar({ fase: 'parada' });
    try { this.alPararFn(nota); } catch (e) { /* la pantalla ya no está */ }
  }

  /** Tirar lo grabado: se para el micrófono y no se entrega nada. */
  descartar() {
    if (this.descartada) return;
    this.descartada = true;
    // Ya no se entrega nada: lo que llegue al parar se ignora.
    const yaTerminada = this.terminada;
    this.terminada = true;
    this.trozos = [];
    clearInterval(this.reloj);
    if (yaTerminada) return;
    try {
      if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    } catch (e) { /* ya estaba parado */ }
    pararPistas(this.stream);
  }
}
